import { useState } from "react";
import { Link } from "react-router-dom";
import { BookHeart, Clock, PenLine, X } from "lucide-react";
import { GuideNoteEditor } from "@/components/GuideNoteEditor";
import { PageHeader, Empty } from "@/components/kit";
import { findArticle } from "@/data/articles";
import { useHelpedGuides } from "@/hooks/useHelpedGuides";

const fmt = (d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const RowNote = ({ g, onSave }) => {
  const [editing, setEditing] = useState(false);
  if (editing) return <div className="px-4 pb-4"><GuideNoteEditor initial={g.note} testPrefix={`guide-note-${g.slug}`} onSave={(t) => onSave(g.slug, t)} onCancel={() => setEditing(false)} /></div>;
  return (
    <div className="px-4 pb-4 flex items-start gap-2 text-sm">
      {g.note && <p className="flex-1 min-w-0 break-words [overflow-wrap:anywhere] mm-soft p-2.5 rounded-xl" data-testid={`guide-note-text-${g.slug}`}>“{g.note}”</p>}
      <button onClick={() => setEditing(true)} className="text-mm-primary font-medium inline-flex items-center gap-1 shrink-0 py-2" data-testid={`guide-note-edit-${g.slug}`}>
        <PenLine size={14} />{g.note ? "Edit" : "Add a note"}
      </button>
    </div>
  );
};

const GuideRow = ({ g, a, onRemove, onSaveNote }) => (
  <div className="mm-card overflow-hidden mm-fade-up" data-testid={`guide-read-${a.slug}`}>
    <div className="flex">
    <Link to={`/articles/${a.slug}`} className="flex flex-1 min-w-0 group">
      <img src={a.image} alt={a.imageAlt} className="w-24 sm:w-36 h-24 sm:h-28 object-cover shrink-0 self-center" />
      <div className="p-4 min-w-0">
        <div className="font-semibold leading-snug group-hover:text-mm-primary transition-colors">{a.title}</div>
        <div className="text-xs text-mm-muted mt-1 inline-flex items-center gap-1"><Clock size={12} />{a.readMins} min read · helped you on {fmt(g.at)}</div>
        <div className="text-sm text-mm-primary font-medium mt-2">Read again →</div>
      </div>
    </Link>
    <button onClick={() => onRemove(a.slug)} className="p-3 self-start text-mm-muted hover:text-mm-error transition-colors" aria-label="Remove guide" data-testid={`guide-read-remove-${a.slug}`}><X size={16} /></button>
    </div>
    <RowNote g={g} onSave={onSaveNote} />
  </div>
);

const ClearButton = ({ onClear }) => {
  const [armed, setArmed] = useState(false);
  if (!armed) return <button onClick={() => setArmed(true)} className="text-mm-muted hover:text-mm-error transition-colors" data-testid="guides-read-clear">Clear list</button>;
  return (
    <span className="flex items-center gap-3" data-testid="guides-read-clear-confirm">
      <span className="text-mm-muted">Remove all guides?</span>
      <button onClick={onClear} className="text-mm-error font-semibold" data-testid="guides-read-clear-yes">Yes, clear</button>
      <button onClick={() => setArmed(false)} className="text-mm-muted" data-testid="guides-read-clear-cancel">Cancel</button>
    </span>
  );
};

export default function GuidesRead() {
  const { list, toggle, clear, saveNote } = useHelpedGuides();
  const rows = (list || []).map((g) => ({ g, a: findArticle(g.slug) })).filter((r) => r.a);
  return (
    <div className="max-w-3xl" data-testid="guides-read-page">
      <PageHeader back title="Guides you've read" subtitle="Guides you marked as helpful, so you can come back to them" />
      {list === null ? null : rows.length ? (
        <>
          <div className="space-y-3">{rows.map(({ g, a }) => <GuideRow key={a.slug} g={g} a={a} onRemove={toggle} onSaveNote={saveNote} />)}</div>
          <div className="flex flex-wrap justify-between items-center gap-3 mt-6 text-sm">
            <Link to="/articles" className="text-mm-primary font-medium" data-testid="guides-read-browse">Browse all guides</Link>
            <ClearButton onClear={clear} />
          </div>
        </>
      ) : (
        <div data-testid="guides-read-empty">
          <Empty emoji="📖" title="No guides saved yet" text={'When a guide helps, tap "This helped" at the end of it and it will show up here.'} />
          <div className="text-center mt-4"><Link to="/articles" className="mm-btn inline-flex" data-testid="guides-read-browse"><BookHeart size={16} />Browse guides</Link></div>
        </div>
      )}
    </div>
  );
}
